import React from "react";
import {Stack, Text, Flex} from "@chakra-ui/core";
import Button from "@pency/components/controls/Button";
import FieldsForm from "../../forms/FieldsForm";

import {DrawerTitle, DrawerBody, DrawerFooter} from "@pency/components/structure/Drawer";
import {
  ClientTenant,
  DeliveryMethod,
  Field,
  MinimumOrderAmountByDeliveryMethod,
} from "~/tenant/types";
import {usePrice, useTranslation} from "~/i18n/hooks";
import ArrowLeftIcon from "@pency/components/icons/ArrowLeft";
import CrossIcon from "@pency/components/icons/Cross";
import { addFinancialCost, getCost, getFinancialCost, getFinancialText, getTotal, hasDelivery, hasFinancialCost } from "~/cart/selectors";
import { CartItem } from "~/cart/types";
import {BrandColors} from "~/ui/colorMode";

const DELIVERY_METHOD_FIELD_ID = "default_delivery_mode";

interface Props {
  fields: Field[];
  items: CartItem[];
  onSubmit: (fields: Field[], formValues, total, getTotalWithDelivery) => void;
  onClose: VoidFunction;
  onPrevious: VoidFunction;
  couponInfo?: {code: string, discount: number, subtotal: number} | null;
  minimumOrderAmount?: ClientTenant["minimumOrderAmount"];
  minimumOrderAmountByDeliveryMethod?: MinimumOrderAmountByDeliveryMethod;
}

function getAmount(amount?: number | string | null) {
  const parsedAmount = typeof amount === "string" ? Number(amount) : amount;

  return typeof parsedAmount === "number" && !Number.isNaN(parsedAmount)
    ? parsedAmount
    : undefined;
}

function isDeliveryMethod(value?: string): value is DeliveryMethod {
  return value === "withdraw_on_point" || value === "home_delivery";
}

function getDeliveryMethod(formValues, fields: Field[], t): DeliveryMethod | undefined {
  const deliveryIndex = fields.findIndex((field) => field.id === DELIVERY_METHOD_FIELD_ID);
  if (deliveryIndex < 0) return undefined;

  const deliveryField = fields[deliveryIndex];
  const selectedValue =
    formValues?.fields?.[deliveryIndex] ??
    formValues?.[`fields[${deliveryIndex}]`];

  if (isDeliveryMethod(selectedValue)) {
    return selectedValue;
  }

  const selectedOption = deliveryField?.options?.find(
    (option) => selectedValue === t(option.title) || selectedValue === option.title
  );

  const optionValue = selectedOption?.value ?? selectedOption?.id;

  return isDeliveryMethod(optionValue)
    ? optionValue
    : undefined;
}

function getMinimumOrderAmount(
  deliveryMethod: DeliveryMethod | undefined,
  minimumOrderAmountByDeliveryMethod:
    | MinimumOrderAmountByDeliveryMethod
    | undefined,
  minimumOrderAmount: ClientTenant["minimumOrderAmount"]
) {
  const amountByDeliveryMethod = deliveryMethod
    ? minimumOrderAmountByDeliveryMethod?.[deliveryMethod]
    : undefined;
  const requiredAmount = getAmount(amountByDeliveryMethod);
  const fallbackAmount = getAmount(minimumOrderAmount);

  if (requiredAmount !== undefined) return requiredAmount;
  if (fallbackAmount !== undefined) return fallbackAmount;

  return 0;
}

const Fields: React.FC<Props> = ({fields, items, onSubmit, onClose, onPrevious, couponInfo, minimumOrderAmount, minimumOrderAmountByDeliveryMethod}) => {
  const [isLoading, toggleLoading] = React.useState(false);
  const t = useTranslation();
  const p = usePrice();

  function handleSubmit(
    event: React.MouseEvent,
    submit: () => Promise<void>,
    meetsMinimumOrder: boolean
  ) {
    event.stopPropagation();

    if (!meetsMinimumOrder) return;

    toggleLoading(true);

    submit().finally(() => toggleLoading(false));
  }

  const total = getTotal(items)

  const getTotalWithDelivery = (formValues): number => {
    const delivery = hasDelivery(formValues, t, fields)
    const deliveryCost = getCost(fields)
    const {financialCost} = addFinancialCost(formValues, total)
    let cost = total

    if (financialCost) {
      cost = financialCost
    }

    if (delivery) {
      cost = cost + deliveryCost
    }
    
    return cost
  }

  return (
    <FieldsForm defaultValues={fields} onSubmit={onSubmit} total={total} getTotalWithDelivery={getTotalWithDelivery}>
      {({form, submit, formValues}) => {
          console.log("Fields formValues", formValues);

          const deliveryMethod = getDeliveryMethod(formValues, fields, t);
          const requiredMinimumOrderAmount = getMinimumOrderAmount(
            deliveryMethod,
            minimumOrderAmountByDeliveryMethod,
            minimumOrderAmount
          );
          const orderTotal = couponInfo
            ? total - couponInfo.discount
            : total;
          const meetsMinimumOrder =
            !requiredMinimumOrderAmount ||
            orderTotal >= requiredMinimumOrderAmount;
          console.log("Minimum order validation", {
            deliveryMethod,
            minimumOrderAmountByDeliveryMethod,
            minimumOrderAmount,
            requiredMinimumOrderAmount,
            orderTotal,
            meetsMinimumOrder,
          });

          return (
            <BrandColors>
          <DrawerBody overflowY="auto">
            <ArrowLeftIcon
              background="card"
              boxShadow="md"
              cursor="pointer"
              left={0}
              marginTop={4}
              paddingX={4}
              paddingY={3}
              position="absolute"
              roundedRight="lg"
              top={0}
              onClick={onPrevious}
            />
            <CrossIcon
              background="card"
              boxShadow="md"
              cursor="pointer"
              marginTop={4}
              paddingX={4}
              paddingY={3}
              position="absolute"
              right={0}
              roundedLeft="lg"
              top={0}
              onClick={onClose}
            />
            <Stack marginTop={20} spacing={6}>
              <DrawerTitle>{t("cart.completeOrder")}</DrawerTitle>
              {form}              
            </Stack>
          </DrawerBody>
          <DrawerFooter borderTopColor="border" borderTopWidth={1} marginTop={2}>
            <Stack spacing={4} width="100%">
            {couponInfo && (
                <Flex alignItems="center" fontSize="md" justifyContent="space-between">
                <Text color="muted">{t("cart.subtotal")}</Text>
                <Text color="muted">{p(couponInfo.subtotal)}</Text>
              </Flex>
              )}
              {couponInfo && (
                <Flex alignItems="center" fontSize="md" justifyContent="space-between">
                <Text color="successText">{t("cart.discount")}</Text>
                <Text color="successText">-{p(couponInfo.discount)}</Text>
              </Flex>
              )}
            {hasFinancialCost(formValues) && (
                <Flex alignItems="center" fontSize="lg" fontWeight={500} justifyContent="space-between">
                <Text>{getFinancialText(formValues)}</Text>
                <Text>{p(getFinancialCost(formValues, total))}</Text>
              </Flex>
              )}
              {hasDelivery(formValues, t, fields) && (
                <Flex alignItems="center" fontSize="lg" fontWeight={500} justifyContent="space-between">
                <Text>{'Envio'}</Text>
                <Text>{p(getCost(fields))}</Text>
              </Flex>
              )}
              <Flex alignItems="center" fontSize="lg" fontWeight={500} justifyContent="space-between">
                <Text>{t("cart.estimatedTotal")}</Text>
                <Text>{p(couponInfo ? (getTotalWithDelivery(formValues) - couponInfo.discount) : getTotalWithDelivery(formValues))}</Text>
              </Flex>
              {!meetsMinimumOrder && (
                <Stack
                  isInline
                  alignItems="center"
                  bg="warningBgSoft"
                  padding="12px"
                  borderRadius="10px"
                >
                  <Text color="warningText" fontSize="sm">
                    {t("cart.minimumOrderAmount")}:{" "}
                    {p(requiredMinimumOrderAmount)}
                  </Text>
                </Stack>
              )}
                <Button 
                  boxShadow="lg" 
                  size="lg" 
                  isLoading={isLoading}
                  isDisabled={!meetsMinimumOrder}
                  variantColor="primary" 
                  onClick={(event) =>
                    handleSubmit(event, submit, meetsMinimumOrder)
                  }>
                    {t("cart.placeOrder")}
                </Button>
            </Stack>
          </DrawerFooter>
            </BrandColors>
          );
        }}
    </FieldsForm>
  );
};

export default Fields;
